import axios, { AxiosError } from "axios";

import {
    RoomListModel,
    RoomDetailModel,
    RoomCreateUpdateModel,
    RoomFilter,
    RoomAndDistance,
    RoomAndDistanceResult,
    RoomAndDistanceFailedGeocodeResult
} from "./model";

class RoomApi {

    private readonly apiUrl = "/api/room";
    private getAllAbortController = new AbortController();
    private getDistancesAbortController = new AbortController();

    public async getAll(filter: RoomFilter): Promise<RoomListModel[]> {
        this.getAllAbortController.abort("Cancelled to allow subsequent request");
        this.getAllAbortController = new AbortController();

        const response = await axios.get(this.apiUrl, { params: filter, signal: this.getAllAbortController.signal });
        const data = response.data as RoomListModel[];
        return data.map(this.parseListModel);
    }

    public async getDistances(postcode: string, distance: number): Promise<RoomAndDistanceResult | RoomAndDistanceFailedGeocodeResult> {
        this.getDistancesAbortController.abort("Cancelled to allow subsequent request");
        this.getDistancesAbortController = new AbortController();

        try {
            const response = await axios.get(
                `${this.apiUrl}/distances`,
                { params: { postcode, distance }, signal: this.getDistancesAbortController.signal }
            );

            return {
                roomsAndDistances: response.data as RoomAndDistance[],
                geocodedSuccessful: true
            };
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError && axiosError.response && axiosError.response.status === 404) {
                return {
                    geocodedSuccessful: false
                };
            }

            throw ex;
        }
    }

    public async detail(id: any): Promise<RoomDetailModel> {
        const response = await axios.get(`${this.apiUrl}/${id}`);
        const model = response.data as RoomDetailModel;
        return this.parseModel(model);
    }

    public async save(id: string, room: RoomCreateUpdateModel): Promise<RoomDetailModel> {
        const response = await axios.put(`${this.apiUrl}/${id}`, room);
        const model = response.data as RoomDetailModel;
        return this.parseModel(model);
    }

    public async create(room: RoomCreateUpdateModel): Promise<RoomDetailModel> {
        const response = await axios.post(`${this.apiUrl}`, room);
        const model = response.data as RoomDetailModel;
        return this.parseModel(model);
    }

    public delete(id: any): Promise<void> {
        return axios.delete(`${this.apiUrl}/${id}`);
    }

    public parseModel(model: RoomDetailModel): RoomDetailModel {
        return {
            ...model
        };
    }

    public parseListModel(model: RoomListModel): RoomListModel {
        return {
            ...model
        };
    }
}

export const roomApi = new RoomApi();
